import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ApiService } from '@services/api.service';
import { environment } from '@env/environment';

import { PaginatedResultModel, PaginationSearchModel } from '@data-layer/models/common';
import { ManagementShipmentModel, ManagementVoyageSearchModel } from '@data-layer/models/management';
import { LclIsfEsLinkModel, LCLShipmentModel, ShipmentCreateModel } from '@data-layer/models/lcl';
import { LclShipmentCardModel } from '@data-layer/models/lcl/lcl-shipment-card.model';
import { enumIncotermsType } from '@data-layer/enums/lcl/enumIncotermsType';
import { enumShippingRole } from '@data-layer/enums/user';
import { ManagementVoyageMissHistoryModel } from '@data-layer/models/management/lcl/management-voyage-miss-history-model';

@Injectable({
  providedIn: 'root',
})
export class ShipmentRepository {
  constructor(private apiService: ApiService) {}

  public searchManagement(
    params?: ManagementVoyageSearchModel,
    isShowLoader?: boolean,
  ): Observable<PaginatedResultModel<ManagementShipmentModel>> {
    return this.apiService.get<PaginatedResultModel<ManagementShipmentModel>>(
      environment.url.freight.shipmentsManagement,
      { params },
      { isShowLoader },
    );
  }

  public getShipmentManagement(id: number, isShowLoader?: boolean): Observable<ManagementShipmentModel> {
    return this.apiService.get<ManagementShipmentModel>(`${environment.url.freight.shipmentsManagement}/${id}`, null, { isShowLoader });
  }

  public getUserShipmentManagement(id: number, isShowLoader?: boolean): Observable<LCLShipmentModel> {
    return this.apiService.get<LCLShipmentModel>(`${environment.url.freight.shipmentsManagement}/${id}/user-data`, null, {
      isShowLoader,
    });
  }

  public updateManagement(model: ManagementShipmentModel, isShowLoader?: boolean): Observable<ManagementShipmentModel> {
    return this.apiService.put<ManagementShipmentModel>(`${environment.url.freight.shipmentsManagement}/${model.id}`, model, null, {
      isShowLoader,
    });
  }

  public updateOpenPayment(id: number, value: boolean, isShowLoader?: boolean): Observable<ManagementShipmentModel> {
    return this.apiService.put<ManagementShipmentModel>(
      `${environment.url.freight.shipmentsManagement}/${id}/is-open-for-payment`,
      value,
      null,
      {
        isShowLoader,
      },
    );
  }

  public getAll(
    companyId: number,
    params?: PaginationSearchModel,
    isShowLoader?: boolean,
  ): Observable<PaginatedResultModel<LclShipmentCardModel>> {
    return this.apiService.get<PaginatedResultModel<LclShipmentCardModel>>(
      `${environment.url.prefixFreight}companies/${companyId}/shipments`,
      { params },
      { isShowLoader },
    );
  }

  public create(shipment: ShipmentCreateModel, isShowLoader?: boolean): Observable<LCLShipmentModel> {
    return this.apiService.post<LCLShipmentModel>(`${environment.url.freight.shipments}`, shipment, null, {
      isShowLoader,
    });
  }

  public get(id: number, isShowLoader?: boolean): Observable<LCLShipmentModel> {
    return this.apiService.get<LCLShipmentModel>(`${environment.url.freight.shipments}/${id}`, null, { isShowLoader });
  }

  public getByReferenceNumber(referenceNumber: string, isShowLoader?: boolean): Observable<LCLShipmentModel> {
    return this.apiService.get<LCLShipmentModel>(`${environment.url.freight.shipments}/by-reference-number/${referenceNumber}`, null, {
      isShowLoader,
    });
  }

  public update(data: LCLShipmentModel, isShowLoader?: boolean): Observable<LCLShipmentModel> {
    return this.apiService.put<LCLShipmentModel>(`${environment.url.freight.shipments}/${data.id}`, data, null, { isShowLoader });
  }

  public updateIncoterms(
    id: number,
    data: {
      exportLicenseRequested: boolean;
      incoTermsType: enumIncotermsType;
      consigneeId?: number;
      notifyPartyId?: number;
      shipperId?: number;
    },
    isShowLoader?: boolean,
  ): Observable<ManagementShipmentModel> {
    return this.apiService.put<ManagementShipmentModel>(`${environment.url.freight.shipments}/${id}`, data, null, {
      isShowLoader,
    });
  }

  public updateManagementIncoTerms(
    id: number,
    data: { exportLicenseRequested: boolean; incoTermsType: enumIncotermsType; shippingRole: enumShippingRole },
    isShowLoader?: boolean,
  ): Observable<ManagementShipmentModel> {
    return this.apiService.put<ManagementShipmentModel>(`${environment.url.freight.shipmentsManagement}/${id}/inco-terms`, data, null, {
      isShowLoader,
    });
  }

  public linkShipmentsToContainerManagement(
    containerId: number,
    shipments: number[],
    isShowLoader?: boolean,
  ): Observable<ManagementShipmentModel> {
    return this.apiService.post<ManagementShipmentModel>(
      `${environment.url.freight.containersManagement}/${containerId}/shipments`,
      shipments,
      null,
      {
        isShowLoader,
      },
    );
  }

  public setISFData(id: number, data: LclIsfEsLinkModel, isShowLoader?: boolean): Observable<LCLShipmentModel> {
    return this.apiService.put<LCLShipmentModel>(`${environment.url.freight.shipments}/${id}/isf-data`, data, null, {
      isShowLoader,
    });
  }

  public setESData(id: number, data: LclIsfEsLinkModel, isShowLoader?: boolean): Observable<LCLShipmentModel> {
    return this.apiService.put<LCLShipmentModel>(`${environment.url.freight.shipments}/${id}/entry-summary-data`, data, null, {
      isShowLoader,
    });
  }

  public getVoyageMissHistory(id: number, isShowLoader?: boolean): Observable<ManagementVoyageMissHistoryModel[]> {
    return this.apiService.get<ManagementVoyageMissHistoryModel[]>(
      `${environment.url.freight.shipmentsManagement}/${id}/voyage-cutoff-date-miss-history`,
      null,
      { isShowLoader },
    );
  }
}
